import { QueryFunctionContext } from "@tanstack/react-query";
import { apiClient, APIError } from "./client";
import { User, Organization, Query, Request, Product, PerformanceFundOption } from "@/types";

export async function getUser(): Promise<User> {
  try {
    return await apiClient.get<User>("/auth/user");
  } catch (error) {
    if (error instanceof APIError && error.status === 404) {
      throw new Error("UserNotFound");
    }
    throw error;
  }
}

export async function getOrganizations(): Promise<Organization[]> {
  return apiClient.get<Organization[]>("/organizations");
}

export interface ChatPeriodOption {
  importId: string;
  label: string;
}

export async function getChatAumPeriods({ queryKey }: QueryFunctionContext<[string, { organizationId: string }]>) {
  const data = await apiClient.get<{ periods: ChatPeriodOption[] }>("/chat/firm-aum/periods", queryKey[1]);
  return data.periods;
}

export async function getProducts({
  queryKey,
}: QueryFunctionContext<[string, { organizationId: string }]>): Promise<Product[]> {
  const { organizationId } = queryKey[1];
  return apiClient.get<Product[]>("/products", { organizationId });
}

export async function getRequests({
  queryKey,
}: QueryFunctionContext<[string, { organizationId: string; productId: string }]>): Promise<Request[]> {
  const { organizationId, productId } = queryKey[1];
  return apiClient.get<Request[]>(`/products/${productId}/requests`, { organizationId });
}

export async function getQueries({
  queryKey,
}: QueryFunctionContext<[string, { organizationId: string; productId: string; requestId: string }]>): Promise<Query[]> {
  const { organizationId, productId, requestId } = queryKey[1];
  return apiClient.get<Query[]>(`/products/${productId}/requests/${requestId}/queries`, { organizationId });
}

/** Funds with approved reports: of one product, or of every product when productId is null. */
export async function getPerformanceFunds({
  queryKey,
}: QueryFunctionContext<[string, { organizationId: string; productId: string | null }]>): Promise<
  PerformanceFundOption[]
> {
  const { organizationId, productId } = queryKey[1];
  const query = productId ? `?productId=${encodeURIComponent(productId)}` : "";
  const data = await apiClient.get<{ funds: PerformanceFundOption[] }>(`/performance/funds${query}`, {
    organizationId,
  });
  return data.funds;
}
